import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { TrendingUp, Calculator, ArrowRight } from 'lucide-react';

function formatCurrency(n) {
  if (n >= 1_000_000) {
    return `$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 1 : 2)}M`;
  }
  if (n >= 10_000) {
    return `$${Math.round(n / 1000).toLocaleString()}k`;
  }
  return `$${n.toLocaleString()}`;
}

export default function SavingsCalculator() {
  const [orders, setOrders] = useState(1000);
  const [perOrder, setPerOrder] = useState(100);

  const monthly = orders * perOrder;
  const yearly = monthly * 12;

  const data = useMemo(() => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    return months.map((m, i) => ({
      month: m,
      savings: monthly * (i + 1),
    }));
  }, [monthly]);

  return (
    <section id="savings" className="bg-white">
      <div className="container-page py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow">Savings calculator</p>
            <h2 className="mt-3 h-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-balance">
              See what competing carriers means for your bottom line.
            </h2>
            <p className="mt-5 text-lg text-ink-600 text-balance">
              Shippers on Vibe Engine save an average of{' '}
              <span className="font-semibold text-ink-900">$100 per order</span>{' '}
              — measured as the median gap between the first carrier accepted
              and the lowest qualified bid on the same load. Drag the sliders to
              model your own freight volume.
            </p>
            <p className="mt-4 text-sm text-ink-600 text-balance">
              Reporting and spend analysis break down savings by lane, carrier,
              and tender strategy — so operations and finance see the same
              numbers in one place.
            </p>

            <div className="mt-8 space-y-7">
              <SliderField
                label="Orders per month"
                value={orders}
                min={50}
                max={5000}
                step={50}
                format={(v) => v.toLocaleString()}
                onChange={setOrders}
              />
              <SliderField
                label="Avg. savings per order"
                value={perOrder}
                min={25}
                max={300}
                step={5}
                format={(v) => `$${v}`}
                onChange={setPerOrder}
                hint="Vibe customer median vs. first-yes carrier: $100"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="card-surface p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 text-sm font-medium text-ink-700">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-ink-100 text-ink-900">
                    <Calculator className="h-4 w-4" />
                  </span>
                  Projected savings
                </div>
                <span className="pill">
                  <TrendingUp className="h-3 w-3" /> Cumulative
                </span>
              </div>

              <div className="mt-6 grid sm:grid-cols-3 gap-3">
                <Metric label="Per order" value={`$${perOrder}`} />
                <Metric label="Per month" value={formatCurrency(monthly)} highlight />
                <Metric label="Per year" value={formatCurrency(yearly)} />
              </div>

              <div className="mt-6 h-64 -mx-2">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="savingsFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0a0a0a" stopOpacity={0.18} />
                        <stop offset="100%" stopColor="#0a0a0a" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: '#737373', fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: '#e5e5e5' }}
                    />
                    <YAxis
                      tick={{ fill: '#737373', fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: '#e5e5e5' }}
                      tickFormatter={(v) => formatCurrency(v)}
                      width={56}
                    />
                    <Tooltip
                      cursor={{ stroke: '#a3a3a3', strokeDasharray: '3 3' }}
                      contentStyle={{
                        background: '#ffffff',
                        border: '1px solid #e5e5e5',
                        borderRadius: 12,
                        boxShadow: '0 10px 30px -12px rgba(15,23,42,0.18)',
                        fontSize: 12,
                      }}
                      labelStyle={{ color: '#171717', fontWeight: 600 }}
                      formatter={(v) => [formatCurrency(v), 'Saved']}
                    />
                    <Area
                      type="monotone"
                      dataKey="savings"
                      stroke="#0a0a0a"
                      strokeWidth={2}
                      fill="url(#savingsFill)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-2 flex items-center justify-between rounded-xl border border-ink-200 bg-ink-50 px-4 py-3">
                <div className="text-xs text-ink-600">
                  At {orders.toLocaleString()} orders/mo × ${perOrder}/order
                </div>
                <a href="#demo" className="btn-primary text-sm">
                  Get a custom estimate
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderField({ label, value, min, max, step, format, onChange, hint }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-medium text-ink-700">{label}</label>
        <span className="text-base font-semibold text-ink-900 tabular-nums">
          {format(value)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 w-full accent-ink-900"
      />
      <div className="mt-1 flex items-center justify-between text-xs text-ink-500">
        <span>{format(min)}</span>
        {hint ? <span>{hint}</span> : null}
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

function Metric({ label, value, highlight }) {
  return (
    <div
      className={`rounded-xl border px-4 py-4 ${
        highlight
          ? 'bg-ink-900 border-ink-900 text-white'
          : 'bg-white border-ink-200 text-ink-900'
      }`}
    >
      <div className={`text-xs ${highlight ? 'text-white/70' : 'text-ink-500'}`}>
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}
